import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  FileUp,
  Search,
  GitBranch,
  Link2,
  Shield,
  PenTool,
  Library,
  Plug,
  Users,
  UserCog,
  CheckCircle2,
  Lock,
  Activity,
  LineChart,
  type LucideIcon,
} from 'lucide-react'
import { Container } from '../../layout/Container'
import { Card } from '../../ui/Card'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'

const iconMap: Record<string, LucideIcon> = {
  fileUp: FileUp,
  search: Search,
  gitBranch: GitBranch,
  link2: Link2,
  shield: Shield,
  penTool: PenTool,
  library: Library,
  plug: Plug,
  users: Users,
  userCog: UserCog,
  checkCircle: CheckCircle2,
  lock: Lock,
  activity: Activity,
  lineChart: LineChart,
}

interface FeatureItem {
  id: string
  title: string
  description: string
  icon: string
}

interface FeatureGridProps {
  tKey: 'brain' | 'build' | 'govern'
}

export function FeatureGrid({ tKey }: FeatureGridProps) {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const items = t(`platformPages.${tKey}.features.items`, { returnObjects: true }) as FeatureItem[]

  return (
    <section ref={ref} className="border-y border-border bg-surface py-24">
      <Container size="wide">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t(`platformPages.${tKey}.features.label`)}
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl">
            {t(`platformPages.${tKey}.features.title`)}
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, index) => {
            const Icon = iconMap[item.icon] ?? Search
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Card variant="elevated" className="h-full">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent-light">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-medium text-text-primary">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-text-secondary">{item.description}</p>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
